/**
 * Typed fetch wrapper around the Fastify API.
 *
 * The access token lives in memory only. The refresh token is an httpOnly
 * cookie, so `POST /auth/refresh` is a credentialed request with an empty body.
 * A 401 triggers exactly one refresh, shared by every request that raced into
 * the same expiry.
 */

const API_BASE = `${import.meta.env.VITE_API_BASE ?? ''}/api`

export interface ApiErrorDetail {
  /** Dotted field path from a Zod issue, e.g. `lines.0.unitPrice`. */
  path?: string
  /** The invoice § 11 check names its field this way instead. */
  field?: string
  severity?: 'error' | 'warning'
  message: string
}

/** The `{ error: { code, message, details } }` envelope, unwrapped. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Validation issues keyed by field path, ready for a form. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {}
    if (!Array.isArray(this.details)) return result
    for (const entry of this.details as ApiErrorDetail[]) {
      // Zod issues carry `path`, the §-11 invoice check carries `field`.
      const key = entry?.path ?? entry?.field
      if (entry && typeof entry.message === 'string' && key) result[key] = entry.message
    }
    return result
  }

  get isValidation(): boolean {
    return this.status === 422 || this.code === 'validation_failed'
  }

  get isOffline(): boolean {
    return this.code === 'network_error'
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

// --- Session state -----------------------------------------------------------

let accessToken: string | null = null
let refreshInFlight: Promise<string | null> | null = null
let onSessionLost: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

/** Called when a refresh fails — the auth store uses it to drop the session. */
export function onUnauthenticated(handler: (() => void) | null): void {
  onSessionLost = handler
}

async function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return null
      const data = (await response.json()) as { accessToken?: string; access_token?: string }
      accessToken = data.accessToken ?? data.access_token ?? null
      return accessToken
    } catch {
      return null
    } finally {
      // Release on the next tick so concurrent callers all observe this result.
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()
  return refreshInFlight
}

/** Exposed for the auth store's `bootstrap()`. */
export async function refreshSession(): Promise<string | null> {
  const token = await refreshAccessToken()
  if (!token) accessToken = null
  return token
}

// --- Request plumbing --------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined | (string | number)[]
export type QueryParams = Record<string, QueryValue>

export interface RequestOptions {
  query?: QueryParams
  body?: unknown
  headers?: Record<string, string>
  signal?: AbortSignal
  /** Do not attach the access token (login, register, refresh). */
  anonymous?: boolean
  /** Do not attempt the refresh-and-retry dance on a 401. */
  noRetry?: boolean
}

export function buildUrl(path: string, query?: QueryParams): string {
  const url = path.startsWith('http')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }
  const search = params.toString()
  return search ? `${url}?${search}` : url
}

function authHeaders(options: RequestOptions): Record<string, string> {
  if (options.anonymous || !accessToken) return {}
  return { Authorization: `Bearer ${accessToken}` }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'request_failed'
  let message = 'Die Anfrage ist fehlgeschlagen.'
  let details: unknown
  try {
    const payload = (await response.json()) as {
      error?: { code?: string; message?: string; details?: unknown }
    }
    if (payload.error) {
      code = payload.error.code ?? code
      message = payload.error.message ?? message
      details = payload.error.details
    }
  } catch {
    if (response.status === 404) message = 'Der Endpunkt existiert nicht.'
    if (response.status >= 500) message = 'Es ist ein unerwarteter Fehler aufgetreten.'
  }
  return new ApiError(response.status, code, message, details)
}

async function parseBody<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) return (await response.json()) as T
  return (await response.text()) as unknown as T
}

export async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const hasBody = options.body !== undefined && options.body !== null
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(options),
      ...options.headers,
    },
    ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), init)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError(0, 'network_error', 'Keine Verbindung zum Server.')
  }

  if (response.status === 401 && !options.anonymous && !options.noRetry) {
    const token = await refreshAccessToken()
    if (!token) {
      accessToken = null
      onSessionLost?.()
      throw await toApiError(response)
    }
    return request<T>(method, path, { ...options, noRetry: true })
  }

  if (!response.ok) throw await toApiError(response)
  return parseBody<T>(response)
}

export const get = <T>(path: string, query?: QueryParams, options?: RequestOptions): Promise<T> =>
  request<T>('GET', path, { ...options, ...(query ? { query } : {}) })

export const post = <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
  request<T>('POST', path, { ...options, body })

export const patch = <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
  request<T>('PATCH', path, { ...options, body })

export const put = <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
  request<T>('PUT', path, { ...options, body })

export const del = <T>(path: string, options?: RequestOptions): Promise<T> =>
  request<T>('DELETE', path, options)

// --- Multipart upload --------------------------------------------------------

export interface UploadOptions {
  /** 0..1 */
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
  fieldName?: string
}

function uploadOnce<T>(
  path: string,
  files: File[] | FileList,
  fields: Record<string, string | number | boolean | undefined>,
  options: UploadOptions,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const form = new FormData()
    const fieldName = options.fieldName ?? 'files'
    // Fields before files: the server reads them while the file part streams, and
    // a field that arrives after the file is not there yet when it is asked for.
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) form.append(key, String(value))
    }
    for (const file of Array.from(files)) form.append(fieldName, file, file.name)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', buildUrl(path))
    xhr.withCredentials = true
    xhr.setRequestHeader('Accept', 'application/json')
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded / event.total)
    }
    xhr.onerror = () => reject(new ApiError(0, 'network_error', 'Upload fehlgeschlagen.'))
    xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'))
    xhr.onload = () => {
      const raw = xhr.responseText
      if (xhr.status >= 200 && xhr.status < 300) {
        options.onProgress?.(1)
        resolve(raw ? (JSON.parse(raw) as T) : (undefined as T))
        return
      }
      let code = 'upload_failed'
      let message = 'Upload fehlgeschlagen.'
      let details: unknown
      try {
        const payload = JSON.parse(raw) as {
          error?: { code?: string; message?: string; details?: unknown }
        }
        code = payload.error?.code ?? code
        message = payload.error?.message ?? message
        details = payload.error?.details
      } catch {
        /* keep the defaults */
      }
      reject(new ApiError(xhr.status, code, message, details))
    }

    options.signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(form)
  })
}

/** Multipart upload with progress. Retries once after a transparent refresh. */
export async function upload<T>(
  path: string,
  files: File[] | FileList,
  fields: Record<string, string | number | boolean | undefined> = {},
  options: UploadOptions = {},
): Promise<T> {
  try {
    return await uploadOnce<T>(path, files, fields, options)
  } catch (error) {
    if (isApiError(error) && error.status === 401) {
      const token = await refreshAccessToken()
      if (!token) {
        accessToken = null
        onSessionLost?.()
        throw error
      }
      return uploadOnce<T>(path, files, fields, options)
    }
    throw error
  }
}

// --- Blob download -----------------------------------------------------------

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const plain = /filename="?([^";]+)"?/i.exec(header)
  return plain?.[1] ?? null
}

/** Fetches a binary response and hands it to the browser as a download. */
export async function download(
  path: string,
  filename?: string,
  query?: QueryParams,
): Promise<void> {
  const blob = await fetchBlob(path, query)
  const url = URL.createObjectURL(blob.blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename ?? blob.filename ?? 'download'
  document.body.append(anchor)
  anchor.click()
  anchor.remove()
  // Revoking immediately aborts the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export interface FetchedBlob {
  blob: Blob
  filename: string | null
}

/** Same auth handling as `request`, but keeps the body as a Blob (PDF previews). */
export async function fetchBlob(
  path: string,
  query?: QueryParams,
  retry = true,
): Promise<FetchedBlob> {
  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      credentials: 'include',
      headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
    })
  } catch {
    throw new ApiError(0, 'network_error', 'Keine Verbindung zum Server.')
  }

  if (response.status === 401 && retry) {
    const token = await refreshAccessToken()
    if (!token) {
      accessToken = null
      onSessionLost?.()
      throw await toApiError(response)
    }
    return fetchBlob(path, query, false)
  }
  if (!response.ok) throw await toApiError(response)

  return {
    blob: await response.blob(),
    filename: filenameFromDisposition(response.headers.get('content-disposition')),
  }
}

export const api = {
  get,
  post,
  patch,
  put,
  del,
  request,
  upload,
  download,
  fetchBlob,
  buildUrl,
}
